import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { CircleCheck, Info, TriangleAlert, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

export type ToastTone = "neutral" | "success" | "danger";

export interface ToastAction {
  readonly label: string;
  readonly run: () => void;
}

export interface NotifyOptions {
  readonly tone?: ToastTone | undefined;
  readonly action?: ToastAction | undefined;
  readonly durationMs?: number | undefined;
}

export interface ToastApi {
  readonly notify: (message: string, options?: NotifyOptions) => void;
}

interface ToastItem {
  readonly id: number;
  readonly message: string;
  readonly tone: ToastTone;
  readonly action: ToastAction | undefined;
}

const MAX_VISIBLE = 3;
const ToastContext = createContext<ToastApi | null>(null);

const TONE_ICON: Record<ToastTone, { readonly icon: LucideIcon; readonly className: string }> = {
  neutral: { icon: Info, className: "text-mist" },
  success: { icon: CircleCheck, className: "text-flow" },
  danger: { icon: TriangleAlert, className: "text-buoy" }
};

export function ToastProvider(props: { readonly children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number): void => {
    setItems((prev: ToastItem[]): ToastItem[] => prev.filter((item: ToastItem): boolean => item.id !== id));
  }, []);

  const notify = useCallback(
    (message: string, options: NotifyOptions = {}): void => {
      const id = nextId.current;
      nextId.current += 1;
      const action = options.action;
      const tone = options.tone ?? "neutral";
      setItems((prev: ToastItem[]): ToastItem[] => [...prev.slice(-(MAX_VISIBLE - 1)), { id, message, tone, action }]);
      window.setTimeout((): void => dismiss(id), options.durationMs ?? (action !== undefined ? 8000 : 4500));
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>((): ToastApi => ({ notify }), [notify]);

  return (
    <ToastContext.Provider value={api}>
      {props.children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[70] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-6"
      >
        {items.map((item: ToastItem): ReactNode => {
          const tone = TONE_ICON[item.tone];
          const Icon = tone.icon;
          return (
            <div
              key={item.id}
              className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-lg border border-line-strong bg-ink-3 py-2.5 pl-3.5 pr-1.5 shadow-pop animate-fade-up"
            >
              <Icon size={16} className={`shrink-0 ${tone.className}`} aria-hidden="true" />
              <p className="min-w-0 flex-1 text-sm leading-snug text-foam">{item.message}</p>
              {item.action !== undefined ? (
                <button
                  type="button"
                  onClick={(): void => {
                    item.action?.run();
                    dismiss(item.id);
                  }}
                  className="inline-flex h-7 shrink-0 items-center rounded-md bg-flow px-3 text-[0.8125rem] font-semibold text-ink transition-colors hover:bg-flow-bright"
                >
                  {item.action.label}
                </button>
              ) : null}
              <button
                type="button"
                onClick={(): void => dismiss(item.id)}
                aria-label="Mitteilung schließen"
                className="grid size-7 shrink-0 place-items-center rounded-md text-mist transition-colors hover:bg-foam/[0.06] hover:text-foam"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error("useToast must be used inside ToastProvider.");
  }
  return ctx;
}
